import { getExperimentValue } from '@canvas/client-feature-flags';
import {
  type RovoContentBridgeAPI,
  createRovoBridgeAPIClient,
  type ClientId,
} from '@canvas/rovo-content-bridge-api';
import {
  CONFLUENCE_AFM_CLIENT_ID,
  WHITEBOARDS_CLIENT_ID,
} from '@canvas/whiteboards-bridge-api/types';

import {
  IsPreviewPanelAvailableCommand,
  type IsPreviewPanelAvailableCommandProperties,
} from './commands/is-preview-panel-available';
import {
  OpenPreviewPanelCommand,
  type OpenPreviewPanelCommandProperties,
} from './commands/open-preview-panel';
import {
  PreviewPanelClosedMessage,
  type PreviewPanelClosedMessageProperties,
} from './commands/preview-panel-closed';
import {
  PreviewPanelOpenedMessage,
  type PreviewPanelOpenedMessageProperties,
} from './commands/preview-panel-opened';

interface AFMBridgeClientInput {
  clientId: ClientId;
  bridge: Pick<RovoContentBridgeAPI, 'send' | 'on' | 'off' | 'teardown'>;
}

const PREVIEW_PANEL_AVAILABLE_TIMEOUT = 300;

type PreviewPanelCallback = (
  data: PreviewPanelOpenedMessageProperties | PreviewPanelClosedMessageProperties
) => void;

class PreviewPanelBridge {
  private previewPanelOpenedCallbacks: Set<PreviewPanelCallback> = new Set();
  private previewPanelClosedCallbacks: Set<PreviewPanelCallback> = new Set();

  public constructor(private input: AFMBridgeClientInput) {
    if (
      getExperimentValue('uxHealthDec2025', 'enabled', {
        fireExperimentExposure: false,
      })
    ) {
      this.input.bridge.on(
        this.input.clientId,
        PreviewPanelClosedMessage,
        (data: PreviewPanelClosedMessageProperties): void => {
          this.previewPanelClosedCallbacks.forEach((callback) => callback(data));
        }
      );

      this.input.bridge.on(
        this.input.clientId,
        PreviewPanelOpenedMessage,
        (data: PreviewPanelOpenedMessageProperties): void => {
          this.previewPanelOpenedCallbacks.forEach((callback) => callback(data));
        }
      );
    }

    // Ensure we unregister all command handlers when the page is unloaded to prevent
    // memory leaks when navigating
    const clientId = this.input.clientId;
    const cleanupBridge = () => {
      this.input.bridge.teardown(clientId);

      window.removeEventListener('beforeunload', cleanupBridge);
      window.removeEventListener('unload', cleanupBridge);
      window.removeEventListener('pagehide', cleanupBridge);
    };
    window.addEventListener('beforeunload', cleanupBridge, { once: true });
    window.addEventListener('unload', cleanupBridge, { once: true });
    window.addEventListener('pagehide', cleanupBridge, { once: true });
  }

  public openPreviewPanel(props: Pick<OpenPreviewPanelCommandProperties, 'url' | 'ari'>) {
    this.input.bridge.send(OpenPreviewPanelCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      url: props.url,
      ari: props.ari,
    });
  }

  public async isPreviewPanelAvailable(
    props: Pick<IsPreviewPanelAvailableCommandProperties, 'ari'>
  ): Promise<boolean | undefined> {
    const commandResult = await this.input.bridge.send(IsPreviewPanelAvailableCommand, {
      senderId: this.input.clientId,
      receiverId: CONFLUENCE_AFM_CLIENT_ID,
      ari: props.ari,
    });

    let timeoutId: ReturnType<typeof setTimeout>;
    let timeoutWon = false;

    const timeoutPromise = new Promise<boolean>((resolve) => {
      timeoutId = setTimeout(() => {
        timeoutWon = true;
        resolve(false);
      }, PREVIEW_PANEL_AVAILABLE_TIMEOUT);
    });
    const resultPromise = commandResult.result().then((result) => {
      clearTimeout(timeoutId);
      return result ?? false;
    });

    const result = await Promise.race([resultPromise, timeoutPromise]);

    if (timeoutWon) {
      commandResult.cancel();
    }

    return result;
  }

  public onPreviewPanelClosed(
    callback: (data: PreviewPanelClosedMessageProperties) => void
  ): () => void {
    this.previewPanelClosedCallbacks.add(callback);
    return () => {
      this.previewPanelClosedCallbacks.delete(callback);
    };
  }

  public onPreviewPanelOpened(
    callback: (data: PreviewPanelOpenedMessageProperties) => void
  ): () => void {
    this.previewPanelOpenedCallbacks.add(callback);
    return () => {
      this.previewPanelOpenedCallbacks.delete(callback);
    };
  }
}

let previewPanelBridge: PreviewPanelBridge;

/**
 * Get the singleton AFM bridge client instance
 */
export function getPreviewPanelBridgeClient() {
  if (!previewPanelBridge) {
    previewPanelBridge = new PreviewPanelBridge({
      clientId: WHITEBOARDS_CLIENT_ID,
      bridge: createRovoBridgeAPIClient(WHITEBOARDS_CLIENT_ID, () => {}),
    });
  }

  return previewPanelBridge;
}

export {
  type PreviewPanelClosedMessageProperties,
  type PreviewPanelOpenedMessageProperties,
};
